import type { Schemas } from "@shared/api/types";
import type { InvoiceSubmission } from "../api/invoiceApi";

/** Union real devuelta por `getAllSubmissions()` (ver `api/invoiceApi.ts`): la
 * misma `InvoiceSubmissionDTO`, que ya declara `invoice?: InvoiceDTO` opcional. */
export type SubmissionUnion = InvoiceSubmission;

/** Submission "envuelta": trae su propia factura digital ya generada. */
type WrappedSubmission = Schemas["InvoiceSubmissionDTO"] & { invoice: Schemas["InvoiceDTO"] };

export const isWrapped = (item: SubmissionUnion): item is WrappedSubmission => item.invoice != null;

/**
 * Fila de la tabla de facturas (`InvoicePage`), tal como la arma `transformToDTO`.
 * `type` (no `interface`) a propósito: TS solo infiere el índice de string implícito
 * que `DataGridTable`/`GridRowLike` (`Record<string, unknown>`) necesita para tipos
 * anónimos/alias, no para interfaces — mismo patrón que los tipos `Schemas["..."]`
 * generados que ya usa el resto del módulo.
 */
export type InvoiceRow = {
  id: number;
  sentDate?: string;
  invoiceDate?: string;
  client?: string;
  description?: string;
  unitPrice?: string | number;
  month?: number;
  year?: number;
  discount: number;
  total?: number;
  currency?: string;
  status?: string;
  types?: string;
  subTypes?: string;
  invoiceId?: number;
};

/**
 * Aplana una submission (digital "envuelta" en `invoice`, o manual/plana) a una fila
 * de tabla. Extraído tal cual de `InvoicePage.jsx` (comportamiento preexistente, sin
 * cambios): se preservan a propósito los `||`/`??`/ternarios inconsistentes de cada
 * campo, incluyendo dos bugs ya documentados en el diseño de esta migración:
 *
 * - `id`: si `item.id` es falsy (0/undefined) y `item.invoice` no existe, el acceso
 *   `item.invoice.id` lanza `TypeError` en runtime. No se agrega guard nuevo.
 * - `client`/`description`/`unitPrice`: `detailList` es opcional en `InvoiceDTO`; si
 *   `item.invoice` existe pero `detailList` es undefined, el `.map` también lanza
 *   `TypeError`. Mismo comportamiento preexistente, sin guard nuevo.
 */
export const transformToDTO = (items: SubmissionUnion[]): InvoiceRow[] =>
  items.map((item) => ({
    id: item.id ? item.id : item.invoice!.id!,
    sentDate: item.invoice ? item.invoice.createdAt : item.sentDate,
    invoiceDate: item.invoiceDate,
    client: item.invoice?.detailList!.map((detail) => detail.client).join(", ") || item.consultantName,
    description: item.invoice?.detailList!.map((detail) => detail.description).join(", ") || item.comments,
    unitPrice: item.invoice?.detailList!.map((detail) => detail.unitPrice).join(", ") || item.total,
    month: item.month,
    year: item.year,
    discount: item.invoice?.discount || 0,
    total: item.invoice?.total || item.total,
    currency: item.currency,
    status: item.invoice?.status || item.status,
    types: item.types,
    subTypes: item.subTypes,
    invoiceId: item.invoice?.id ?? item.id,
  }));
